import { apiGet } from './apiClient';

function escapeCsv(value) {
  const normalized = value == null ? '' : String(value);
  return `"${normalized.replace(/"/g, '""')}"`;
}

function normalizeQuantity(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'number') {
    const roundedInt = Math.round(value);
    if (Math.abs(value - roundedInt) < 1e-9) return String(roundedInt);
    return String(Math.round(value * 100) / 100);
  }
  return String(value);
}

function mealNumberLabel(value) {
  switch (Number(value)) {
    case 1:
      return 'Breakfast';
    case 2:
      return 'Lunch';
    case 3:
      return 'Dinner';
    default:
      return '';
  }
}

function joinIngredients(ingredients = []) {
  return ingredients
    .map((ingredient) => {
      const qty = normalizeQuantity(ingredient.quantity);
      const unit = ingredient.unit || ingredient.units || '';
      const name = ingredient.name || ingredient.ingredient || '';
      return [qty, unit, name].filter(Boolean).join(' ').trim();
    })
    .filter(Boolean)
    .join('; ');
}

function joinMethod(method) {
  if (Array.isArray(method)) {
    return method
      .map((step) => (typeof step === 'string' ? step : step?.text || ''))
      .filter(Boolean)
      .join(' | ');
  }
  return typeof method === 'string' ? method : '';
}

function buildRecipesCsv(recipes) {
  const header = ['Name', 'Meal Type', 'Servings', 'Prep Time', 'Cook Time', 'Tags', 'Ingredients', 'Method', 'Source'];
  const rows = recipes.map((recipe) => [
    recipe.name || recipe.title || '',
    recipe.mealType || '',
    normalizeQuantity(recipe.servings),
    normalizeQuantity(recipe.prepTime),
    normalizeQuantity(recipe.cookTime),
    Array.isArray(recipe.tags) ? recipe.tags.join(', ') : '',
    joinIngredients(recipe.ingredients),
    joinMethod(recipe.method || recipe.instructions || recipe.steps),
    recipe.source || recipe.sourceUrl || '',
  ]);
  return [header, ...rows].map((row) => row.map(escapeCsv).join(',')).join('\n');
}

function buildRecipesText(recipes) {
  return recipes.map((recipe) => {
    const lines = [
      recipe.name || recipe.title || 'Untitled recipe',
      `Meal type: ${recipe.mealType || 'N/A'}`,
      `Servings: ${normalizeQuantity(recipe.servings) || 'N/A'}`,
      `Prep time: ${normalizeQuantity(recipe.prepTime) || 'N/A'}`,
      `Cook time: ${normalizeQuantity(recipe.cookTime) || 'N/A'}`,
      `Tags: ${Array.isArray(recipe.tags) && recipe.tags.length ? recipe.tags.join(', ') : 'None'}`,
      `Ingredients: ${joinIngredients(recipe.ingredients) || 'None'}`,
      `Method: ${joinMethod(recipe.method || recipe.instructions || recipe.steps) || 'None'}`,
      `Source: ${recipe.source || recipe.sourceUrl || 'N/A'}`,
    ];
    return lines.join('\n');
  }).join('\n\n--------------------------------\n\n');
}

function buildMealPlansCsv(plans) {
  const header = ['Week Start', 'Assigned Date', 'Day', 'Meal', 'Recipe', 'Serves', 'Tags'];
  const rows = plans.map((plan) => [
    plan.weekStartDate || '',
    plan.assignedDate || '',
    plan.assignedDay || '',
    mealNumberLabel(plan.assignedMealNumber) || plan.mealType || '',
    plan.name || plan.title || '',
    normalizeQuantity(plan.serves ?? plan.servings),
    Array.isArray(plan.tags) ? plan.tags.join(', ') : '',
  ]);
  return [header, ...rows].map((row) => row.map(escapeCsv).join(',')).join('\n');
}

function buildMealPlansText(plans) {
  return plans.map((plan) => {
    const lines = [
      plan.name || plan.title || 'Untitled meal',
      `Week start: ${plan.weekStartDate || 'N/A'}`,
      `Assigned date: ${plan.assignedDate || 'N/A'}`,
      `Day: ${plan.assignedDay || 'N/A'}`,
      `Meal: ${mealNumberLabel(plan.assignedMealNumber) || plan.mealType || 'N/A'}`,
      `Serves: ${normalizeQuantity(plan.serves ?? plan.servings) || 'N/A'}`,
      `Tags: ${Array.isArray(plan.tags) && plan.tags.length ? plan.tags.join(', ') : 'None'}`,
    ];
    return lines.join('\n');
  }).join('\n\n--------------------------------\n\n');
}

function buildShoppingListCsv(items) {
  const header = ['Week Start', 'Category', 'Item', 'Quantity', 'Unit'];
  const rows = items.map((item) => [
    item.weekStartDate || '',
    item.category || '',
    item.name || '',
    normalizeQuantity(item.quantity),
    item.unit || '',
  ]);
  return [header, ...rows].map((row) => row.map(escapeCsv).join(',')).join('\n');
}

function buildShoppingListText(items) {
  return items.map((item) => {
    const qty = [normalizeQuantity(item.quantity), item.unit || ''].filter(Boolean).join(' ').trim();
    const lines = [
      item.name || 'Untitled item',
      `Week start: ${item.weekStartDate || 'N/A'}`,
      `Category: ${item.category || 'N/A'}`,
      `Quantity: ${qty || 'N/A'}`,
    ];
    return lines.join('\n');
  }).join('\n\n--------------------------------\n\n');
}

function downloadFile(filename, content, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.URL.revokeObjectURL(url);
}

const exportConfig = {
  mealPlans: {
    filenameBase: 'meal-plans',
    fetcher: () => apiGet('/weeklyPlans'),
    toCsv: buildMealPlansCsv,
    toText: buildMealPlansText,
  },
  shoppingList: {
    filenameBase: 'shopping-list',
    fetcher: () => apiGet('/shoppingList'),
    toCsv: buildShoppingListCsv,
    toText: buildShoppingListText,
  },
  recipes: {
    filenameBase: 'recipes',
    fetcher: () => apiGet('/recipes'),
    toCsv: buildRecipesCsv,
    toText: buildRecipesText,
  },
};

export async function exportDataSet(dataSetKey, format) {
  const config = exportConfig[dataSetKey];
  if (!config) {
    throw new Error('Unknown export type requested.');
  }

  const data = await config.fetcher();
  const list = Array.isArray(data) ? data : [];
  const timestamp = new Date().toISOString().slice(0, 10);

  if (format === 'csv') {
    downloadFile(`${config.filenameBase}-${timestamp}.csv`, config.toCsv(list), 'text/csv');
    return;
  }

  if (format === 'txt') {
    downloadFile(`${config.filenameBase}-${timestamp}.txt`, config.toText(list), 'text/plain');
    return;
  }

  throw new Error('Unsupported export format requested.');
}